/**
 * UI-обработчики страницы manager-request:
 *  - Чекбоксы бренда ↔ товары (indeterminate когда выбран не весь бренд)
 *  - Счётчик количества (adm_vidget_count) с пересчётом
 *  - Раскрытие детальной информации товара
 *  - Автовыделение текста в числовых полях
 *  - Кнопки: save/approve/delete товара, save-all/approve-all, archive/delete request, export, add products
 */

import { calculateProduct, updateGrandTotals } from './calculator.js';
import { saveProduct, approveProduct, deleteProduct, saveAllProducts, approveAllProducts } from './products.js';
import { archiveRequest, deleteRequest, exportSpecification, openAddProductsModal } from './request.js';
import { showConfirmModal } from './confirm-modal.js';

function updateBrandCheckboxState(productCheckbox) {
    const brandItem = productCheckbox.closest('.adm_req_brand_item');
    if (!brandItem) return;

    const brandCheckbox = brandItem.querySelector('.brand-checkbox');
    if (!brandCheckbox) return;

    const allProducts = brandItem.querySelectorAll('.product-checkbox');
    const checked = brandItem.querySelectorAll('.product-checkbox:checked');

    if (checked.length === 0) {
        brandCheckbox.checked = false;
        brandCheckbox.indeterminate = false;
    } else if (checked.length === allProducts.length) {
        brandCheckbox.checked = true;
        brandCheckbox.indeterminate = false;
    } else {
        brandCheckbox.checked = false;
        brandCheckbox.indeterminate = true;
    }
}

function changeQuantity(trigger, delta) {
    const container = trigger.closest('.adm_vidget_count');
    if (!container) return;

    const valueSpan = container.querySelector('.adm_vidget_value');
    const hiddenInput = container.querySelector('input[type="hidden"]');
    if (!valueSpan || !hiddenInput) return;

    let val = parseInt(valueSpan.textContent, 10) || 1;
    if (delta < 0 && val <= 1) return;

    val += delta;
    valueSpan.textContent = val;
    hiddenInput.value = val;

    const productId = hiddenInput.dataset.productId;
    if (productId) {
        calculateProduct(productId);
        updateGrandTotals();
    }
}

export function initUi() {
    // Раскрытие групп брендов (статический список брендов — querySelectorAll не нужен в делегате)
    document.querySelectorAll('.adm_req_brand_item_name').forEach(el => {
        el.addEventListener('click', () => {
            el.closest('.adm_req_brand_item')?.classList.toggle('opened');
        });
    });

    // Brand-checkbox → все товары бренда
    document.querySelectorAll('.brand-checkbox').forEach(cb => {
        cb.addEventListener('change', () => {
            const brandItem = cb.closest('.adm_req_brand_item');
            if (!brandItem) return;
            brandItem.querySelectorAll('.product-checkbox').forEach(pc => {
                pc.checked = cb.checked;
            });
        });
    });

    // Пересчёт при изменении любого числового инпута
    document.querySelectorAll('input[type="number"]').forEach(input => {
        const handler = () => {
            let productId = input.dataset.productId;
            if (!productId) productId = input.name.split('_').pop();
            calculateProduct(productId);
            updateGrandTotals();
        };
        input.addEventListener('change', handler);
        input.addEventListener('input', handler);
    });

    // Кнопки-singletons (document.getElementById — уже существуют при init)
    const bindById = (id, handler) => {
        const el = document.getElementById(id);
        if (el) el.addEventListener('click', handler);
    };

    bindById('filesBtn', e => {
        e.preventDefault();
        if (typeof window.showLargeModal === 'function') window.showLargeModal('projectFilesModal');
    });

    bindById('btn_save_all', e => {
        e.preventDefault();
        saveAllProducts();
    });

    bindById('btn_approve_all', e => {
        e.preventDefault();
        showConfirmModal(
            'Are you sure you want to send all calculations to the client?',
            () => approveAllProducts()
        );
    });

    bindById('archive-request', e => {
        e.preventDefault();
        const requestId = e.currentTarget.dataset.id;
        showConfirmModal(
            'Are you sure you want to archive this request?',
            () => archiveRequest(requestId)
        );
    });

    bindById('delete-request', e => {
        e.preventDefault();
        const requestId = e.currentTarget.dataset.id;
        showConfirmModal(
            'Are you sure you want to delete this request?',
            () => deleteRequest(requestId)
        );
    });

    bindById('export-specification', e => {
        e.preventDefault();
        exportSpecification();
    });

    bindById('add-products', e => {
        e.preventDefault();
        openAddProductsModal();
    });

    // Делегированные обработчики — для динамического контента и агрегации
    document.addEventListener('focus', e => {
        if (e.target.matches('input[type="number"], input[type="text"].calculator-field')) {
            e.target.select();
        }
    }, true);

    document.addEventListener('change', e => {
        if (e.target.matches('.product-checkbox')) {
            updateBrandCheckboxState(e.target);
        }
    });

    document.addEventListener('click', e => {
        // Раскрытие деталей товара (но не при клике на контролы внутри)
        const row = e.target.closest('.adm_req_brand_item_table_outer');
        if (row) {
            const interactive = e.target.closest('input, button, textarea, a, label, .adm_req_table_content_tools, .adm_vidget_count');
            if (!interactive) {
                row.closest('.adm_req_brand_item_info')?.classList.toggle('expanded');
            }
        }

        // Счётчик количества
        if (e.target.closest('.adm_vidget_minus')) {
            changeQuantity(e.target.closest('.adm_vidget_minus'), -1);
            return;
        }
        if (e.target.closest('.adm_vidget_plus')) {
            changeQuantity(e.target.closest('.adm_vidget_plus'), +1);
            return;
        }

        // Кнопки индивидуальные (save/approve/delete)
        const saveBtn = e.target.closest('.save-product');
        if (saveBtn) {
            e.preventDefault();
            saveProduct(saveBtn.dataset.id);
            return;
        }

        const approveBtn = e.target.closest('.approve-product');
        if (approveBtn) {
            e.preventDefault();
            const productId = approveBtn.dataset.id;
            showConfirmModal(
                'Are you sure you want to send the calculation results to the client?',
                () => approveProduct(productId)
            );
            return;
        }

        const deleteBtn = e.target.closest('.delete-product');
        if (deleteBtn) {
            e.preventDefault();
            const productId = deleteBtn.dataset.id;
            showConfirmModal(
                'Are you sure you want to delete this product?',
                () => deleteProduct(productId)
            );
        }
    });
}
